// DNA CONNECT - Semana de Avaliações
// Visualização do calendário de provas para as famílias

import React, { useState, useEffect } from 'react';
import { 
  Calendar, BookOpen, Clock, AlertCircle, CheckCircle2,
  ChevronLeft, ChevronRight, Info, User
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const API_URL = window.location.origin;

// Cores por disciplina
const CORES_DISCIPLINAS = {
  'Matemática': { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-300' },
  'Português': { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300' },
  'Língua Portuguesa': { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300' },
  'História': { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300' },
  'Geografia': { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300' },
  'Ciências': { bg: 'bg-teal-100', text: 'text-teal-800', border: 'border-teal-300' },
  'Física': { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300' },
  'Química': { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-300' },
  'Biologia': { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300' },
  'Inglês': { bg: 'bg-rose-100', text: 'text-rose-800', border: 'border-rose-300' },
  'Espanhol': { bg: 'bg-pink-100', text: 'text-pink-800', border: 'border-pink-300' },
  'Artes': { bg: 'bg-fuchsia-100', text: 'text-fuchsia-800', border: 'border-fuchsia-300' },
  'Arte': { bg: 'bg-fuchsia-100', text: 'text-fuchsia-800', border: 'border-fuchsia-300' },
  'Educação Física': { bg: 'bg-cyan-100', text: 'text-cyan-800', border: 'border-cyan-300' },
  'Filosofia': { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-300' },
  'Sociologia': { bg: 'bg-violet-100', text: 'text-violet-800', border: 'border-violet-300' },
  'Programa Bilíngue': { bg: 'bg-rose-100', text: 'text-rose-800', border: 'border-rose-300' },
  'Produção Textual': { bg: 'bg-lime-100', text: 'text-lime-800', border: 'border-lime-300' },
  'Educação Financeira': { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300' },
};

const getCorDisciplina = (nome) => {
  return CORES_DISCIPLINAS[nome] || { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-300' };
};

const formatarData = (dataStr) => {
  if (!dataStr) return '';
  const d = new Date(dataStr + 'T12:00:00');
  return d.toLocaleDateString('pt-BR', { 
    weekday: 'long', 
    day: '2-digit', 
    month: 'long' 
  });
};

const formatarDataCurta = (dataStr) => {
  if (!dataStr) return '';
  const d = new Date(dataStr + 'T12:00:00');
  return d.toLocaleDateString('pt-BR', { 
    weekday: 'short', 
    day: '2-digit', 
    month: '2-digit' 
  });
};

export default function DnaConnectSemanaAvaliacoes() {
  const [loading, setLoading] = useState(true);
  const [semanas, setSemanas] = useState([]);
  const [alunoSelecionado, setAlunoSelecionado] = useState(null);
  const [alunos, setAlunos] = useState([]);
  const token = localStorage.getItem('familyToken');

  useEffect(() => {
    fetchSemanas();
  }, []);

  const fetchSemanas = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_URL}/api/assessment-week/connect/semana-avaliacoes?token=${token}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setSemanas(data.semanas || []);
        
        // Extrair lista de alunos únicos
        const alunosMap = new Map();
        data.semanas?.forEach(semana => {
          semana.alunos?.forEach(aluno => {
            if (!alunosMap.has(aluno.id)) {
              alunosMap.set(aluno.id, aluno);
            }
          });
        });
        setAlunos(Array.from(alunosMap.values()));
      } else {
        toast.error('Erro ao carregar semana de avaliações');
      }
    } catch (error) {
      console.error('Erro:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // Filtrar semanas por aluno
  const semanasFiltradas = alunoSelecionado 
    ? semanas.filter(s => s.alunos?.some(a => a.id === alunoSelecionado))
    : semanas;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (semanas.length === 0) {
    return (
      <div className="p-4 md:p-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Calendar className="h-16 w-16 text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              Nenhuma Semana de Avaliações
            </h3>
            <p className="text-gray-500 text-center max-w-md">
              Não há semana de avaliações publicada para visualização no momento.
              A escola publicará o calendário de provas quando estiver disponível.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
            <BookOpen className="h-7 w-7 text-purple-600" />
            Semana de Avaliações
          </h1>
          <p className="text-gray-600 mt-1">
            Confira o calendário de provas e avaliações
          </p>
        </div>

        {/* Filtro por aluno */}
        {alunos.length > 1 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Filtrar:</span>
            <select
              className="border rounded-lg px-3 py-2 text-sm bg-white"
              value={alunoSelecionado || ''}
              onChange={(e) => setAlunoSelecionado(e.target.value || null)}
            >
              <option value="">Todos os filhos</option>
              {alunos.map(aluno => (
                <option key={aluno.id} value={aluno.id}>
                  {aluno.nome}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Semanas */}
      {semanasFiltradas.map((semana) => (
        <Card key={semana.id} className="overflow-hidden">
          {/* Header da Semana */}
          <div className="bg-gradient-to-r from-purple-600 to-purple-800 text-white p-4 md:p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
              <div>
                <h2 className="text-xl font-bold">{semana.trimestre}º Trimestre {semana.ano}</h2>
                <p className="text-purple-200">{semana.turma}</p>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                <span className="text-sm">
                  {formatarDataCurta(semana.data_inicio)} até {formatarDataCurta(semana.data_fim)}
                </span>
              </div>
            </div>
            
            {/* Alunos */}
            {semana.alunos && semana.alunos.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {semana.alunos.map(aluno => (
                  <Badge 
                    key={aluno.id} 
                    className="bg-white/20 text-white border-white/30"
                  >
                    <User className="h-3 w-3 mr-1" />
                    {aluno.nome}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <CardContent className="p-0">
            {/* Grade de Avaliações */}
            <div className="overflow-x-auto">
              <div className="min-w-[600px]">
                {Object.entries(semana.avaliacoes_por_data || {}).map(([data, avaliacoes]) => (
                  <div key={data} className="border-b last:border-b-0">
                    {/* Data */}
                    <div className="bg-gray-50 px-4 py-3 flex items-center gap-3">
                      <div className="bg-purple-100 rounded-lg p-2">
                        <Calendar className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 capitalize">
                          {formatarData(data)}
                        </p>
                        <p className="text-sm text-gray-500">
                          {avaliacoes.length} avaliação(ões)
                        </p>
                      </div>
                    </div>

                    {/* Avaliações do dia */}
                    <div className="p-4 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                      {avaliacoes.map((aval, idx) => {
                        const cor = getCorDisciplina(aval.disciplina);
                        return (
                          <div 
                            key={idx}
                            className={`${cor.bg} ${cor.border} border-2 rounded-xl p-4 transition-all hover:shadow-md`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h4 className={`font-bold ${cor.text} text-lg`}>
                                  {aval.disciplina}
                                </h4>
                                <div className="mt-2 space-y-1">
                                  <p className="text-sm text-gray-600 flex items-center gap-2">
                                    <Clock className="h-4 w-4" />
                                    {aval.horario}
                                  </p>
                                  <p className="text-sm text-gray-600">
                                    {aval.aula}
                                  </p>
                                  {aval.tipo && (
                                    <Badge variant="outline" className={`${cor.text} ${cor.border} text-xs`}>
                                      {aval.tipo}
                                    </Badge>
                                  )}
                                </div>
                                {aval.observacoes && (
                                  <p className="mt-2 text-xs text-gray-500 italic">
                                    {aval.observacoes}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Mensagem da escola */}
            {semana.mensagem && (
              <div className="bg-blue-50 border-t border-blue-100 p-4">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-900">Mensagem da Escola</p>
                    <p className="text-blue-700 text-sm mt-1">{semana.mensagem}</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ))}

      {/* Dicas */}
      <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
        <CardContent className="p-4 md:p-6">
          <div className="flex items-start gap-4">
            <div className="bg-amber-100 rounded-full p-3">
              <AlertCircle className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-amber-900">Dicas para a Semana de Provas</h3>
              <ul className="mt-2 space-y-1 text-sm text-amber-800">
                <li>• Organize o material de estudo com antecedência</li>
                <li>• Revise os conteúdos poucos dias antes de cada prova</li>
                <li>• Descanse bem na noite anterior às avaliações</li>
                <li>• Chegue à escola com tempo de sobra</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
